import { normalizeJourneyStage, validateJourneyStageExplanation } from './journeyStageExplanation.js';

export const JOURNEY_BATCH7_EXPLANATIONS = Object.freeze({
  paediatrics: Object.freeze({
    preparing: Object.freeze({
      linkIds: Object.freeze(['visit', 'where', 'support']),
      questionIndexes: Object.freeze([0, 2, 3]),
      roleIds: Object.freeze([
        'childrens_nurse',
        'paediatrician',
        'play_specialist',
        'healthcare_assistant',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before a child or young person comes to hospital, the team may give instructions about medicines, eating or drinking, arrival time and what to bring. Families can also tell the service about communication needs, sensory needs, disabilities, anxiety or other reasonable adjustments that may make the visit easier.',
        whyItMayHappen: 'Preparing in advance can make the visit safer and less unfamiliar. It also gives the team time to understand what helps the child or young person communicate, cope with procedures and take part in decisions.',
        whatTeamIsDoing: 'The team is checking the reason for the visit and making sure the right people and equipment are available. They are planning how information and care can be adapted. This should reflect the child or young person’s age, development and individual needs.',
        variationNote: 'Preparation is different for every visit. Some children need no special preparation, while others may need fasting, medicine changes, extra time, a quieter space, communication support or help from a play specialist. Follow the instructions for this particular appointment or admission.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'attend-as-planned',
            title: 'Attend the visit as planned',
            stageId: 'during-visit',
            when: 'When the preparation instructions are clear and the planned visit can go ahead',
          }),
          Object.freeze({
            id: 'arrange-adjustments',
            title: 'Arrange extra support or reasonable adjustments',
            stageId: 'preparing',
            description: 'This might include communication support, a quieter waiting arrangement, more time, sensory adjustments or help preparing for a procedure.',
            when: 'If the child or young person has needs that could make the visit harder without additional support',
          }),
          Object.freeze({
            id: 'clarify-instructions',
            title: 'Check unclear preparation instructions before coming',
            stageId: 'preparing',
            when: 'If you are unsure about medicines, fasting, arrival time or what support can be provided',
          }),
        ]),
      }),
    }),
    'during-visit': Object.freeze({
      linkIds: Object.freeze(['family', 'support']),
      questionIndexes: Object.freeze([1, 4, 5]),
      roleIds: Object.freeze([
        'childrens_nurse',
        'paediatrician',
        'play_specialist',
        'healthcare_assistant',
        'pharmacist',
        'physiotherapist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'During the visit or stay, staff may ask questions, take observations and examine the child or young person. Depending on the reason for attending, they may need blood tests, a cannula, a scan, medicines or another procedure. The team should explain what is happening in a way the child or young person can understand.',
        whyItMayHappen: 'These checks and treatments help the team understand what is wrong, monitor how the child or young person is doing and decide what care is needed next.',
        whatTeamIsDoing: 'The team is balancing medical care with comfort, communication and family involvement. They may use play, distraction, pain relief or other approaches to make tests and procedures easier. They should involve the child or young person in decisions as much as is appropriate for their age and understanding.',
        variationNote: 'Parents and carers are usually important partners in children’s care, but the exact arrangements depend on the child’s age, needs and the type of care. Some tests or procedures need consent, and older children and young people may also be able to make some decisions for themselves. Ask the team to explain how this applies in your situation.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-care',
            title: 'Continue assessment or treatment',
            stageId: 'during-visit',
            when: 'If more monitoring, treatment, tests or specialist review are still needed',
          }),
          Object.freeze({
            id: 'go-home',
            title: 'Prepare to go home',
            stageId: 'going-home',
            when: 'When hospital care is no longer needed and the child or young person can safely continue recovery or monitoring at home',
          }),
          Object.freeze({
            id: 'admit-or-transfer',
            title: 'Stay in hospital or move to another specialist service',
            when: 'If the child or young person needs inpatient care or more specialist treatment than can be provided in the current area',
          }),
        ]),
      }),
    }),
    'going-home': Object.freeze({
      linkIds: Object.freeze(['support', 'family']),
      questionIndexes: Object.freeze([6]),
      roleIds: Object.freeze([
        'childrens_nurse',
        'paediatrician',
        'pharmacist',
        'play_specialist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before going home, the team should explain any medicines, follow-up, activity or care instructions and what changes should prompt the family to seek advice. If tests are still being processed, the team should explain how and when results are expected to be shared.',
        whyItMayHappen: 'Clear information helps families know what is expected during recovery, what can be managed at home and when the child or young person should be reviewed again.',
        whatTeamIsDoing: 'The team is checking that the child or young person is safe to leave. They are completing prescriptions and follow-up arrangements. They will give safety-netting information about symptoms or concerns that should not wait for a routine appointment.',
        variationNote: 'Some children need no further hospital follow-up, while others need clinic review, community support or ongoing specialist care. Make sure you know who the main contact is and what to do if the child or young person becomes more unwell.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'recover-at-home',
            title: 'Continue care or recovery at home',
            when: 'If the child or young person is improving and the home plan is working as expected',
          }),
          Object.freeze({
            id: 'planned-follow-up',
            title: 'Attend planned follow-up',
            stageId: 'going-home',
            when: 'If the team needs to review recovery, results, treatment or an ongoing condition',
          }),
          Object.freeze({
            id: 'earlier-help',
            title: 'Seek earlier help if concerns develop',
            description: 'Use the contact and safety-netting advice provided by the child’s team, or seek urgent help when appropriate.',
            when: 'If symptoms get worse, something new develops or the child or young person seems significantly more unwell',
          }),
        ]),
      }),
    }),
  }),

  renal: Object.freeze({
    assessment: Object.freeze({
      linkIds: Object.freeze(['appointment', 'tests']),
      questionIndexes: Object.freeze([0, 1]),
      roleIds: Object.freeze([
        'consultant',
        'clinical_nurse_specialist',
        'pharmacist',
        'healthcare_assistant',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Kidney assessment often includes blood pressure, blood tests and urine tests. You may hear about creatinine and eGFR, which are measures the team uses to understand kidney function. Depending on the problem, you may also have an ultrasound or another scan and a review of your medicines.',
        whyItMayHappen: 'Kidneys remove waste and extra fluid from the blood and help control several body functions. Tests help the team see how well the kidneys are working, whether the change is new or long term, and what may be causing it.',
        whatTeamIsDoing: 'The team is comparing your results over time, checking fluid balance and blood pressure, reviewing medicines that can affect kidney function and deciding whether more tests or specialist treatment are needed.',
        variationNote: 'A single kidney blood-test result does not always show the whole picture. Kidney function can change with illness, hydration, medicines and other conditions, so the team often looks at trends and your wider clinical situation rather than one number alone.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'monitor-kidney-function',
            title: 'Continue monitoring kidney function',
            stageId: 'assessment',
            when: 'If the team needs repeat blood or urine tests before deciding whether treatment should change',
          }),
          Object.freeze({
            id: 'treatment-plan',
            title: 'Start or adjust a kidney-care treatment plan',
            stageId: 'treatment',
            when: 'When the cause and severity are clear enough to guide medicines, fluid advice or other treatment',
          }),
          Object.freeze({
            id: 'more-investigation',
            title: 'Have further tests or another specialist review',
            stageId: 'assessment',
            when: 'If the first tests do not fully explain the kidney problem or another condition may be contributing',
          }),
        ]),
      }),
    }),
    treatment: Object.freeze({
      linkIds: Object.freeze(['treatment']),
      questionIndexes: Object.freeze([2, 4, 5]),
      roleIds: Object.freeze([
        'consultant',
        'clinical_nurse_specialist',
        'staff_nurse',
        'pharmacist',
        'dietitian',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Kidney treatment depends on the cause and severity. It may include changing medicines, treating another illness, managing blood pressure, adjusting fluid or dietary intake, or closer monitoring. For some people with severe kidney failure, dialysis may be discussed as one possible way to remove waste and extra fluid from the blood.',
        whyItMayHappen: 'Treatment aims to protect remaining kidney function, correct problems caused by reduced kidney function and manage the underlying cause where possible.',
        whatTeamIsDoing: 'The team is reviewing blood and urine results, medicines, fluid balance, blood pressure and symptoms. If dialysis may be needed, they should explain the different options, what preparation is required and why one approach may suit you better than another.',
        variationNote: 'Not everyone with reduced kidney function needs dialysis, and dietary or fluid advice is not the same for everyone. Do not restrict food, fluid or medicines unless your own kidney team has advised you to do so.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-conservative-treatment',
            title: 'Continue medicines, monitoring and kidney-protection measures',
            stageId: 'treatment',
            when: 'If kidney function can be managed without dialysis and the current plan is appropriate',
          }),
          Object.freeze({
            id: 'dialysis-planning',
            title: 'Discuss or prepare for dialysis',
            stageId: 'treatment',
            description: 'The team may discuss haemodialysis, peritoneal dialysis or other approaches if kidney replacement treatment is being considered.',
            when: 'If kidney function is severely reduced and the team thinks dialysis may be needed',
          }),
          Object.freeze({
            id: 'hospital-treatment',
            title: 'Receive hospital treatment for a more acute kidney problem',
            stageId: 'treatment',
            when: 'If kidney function has worsened quickly or there are complications that need closer treatment or monitoring',
          }),
        ]),
      }),
    }),
    'ongoing-care': Object.freeze({
      linkIds: Object.freeze(['support', 'appointment']),
      questionIndexes: Object.freeze([3, 6]),
      roleIds: Object.freeze([
        'consultant',
        'clinical_nurse_specialist',
        'dietitian',
        'pharmacist',
        'staff_nurse',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Ongoing kidney care may involve regular blood and urine tests, blood-pressure checks, medicine reviews and advice about food or fluid where relevant. Some people are followed mainly in kidney clinics, while others have shared care with their GP or another specialist service.',
        whyItMayHappen: 'Kidney function can change gradually, and treatment often needs adjusting as results, symptoms and other health conditions change.',
        whatTeamIsDoing: 'The team is tracking kidney function over time, reducing avoidable risks to the kidneys, treating complications and helping you understand what symptoms or changes should prompt earlier contact.',
        variationNote: 'The frequency of kidney appointments and tests depends on the cause and stability of your kidney problem. Some people need long-term specialist follow-up; others need less frequent review if kidney function is stable.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'specialist-follow-up',
            title: 'Continue kidney-clinic follow-up',
            stageId: 'ongoing-care',
            when: 'If specialist monitoring, treatment adjustment or dialysis care is still needed',
          }),
          Object.freeze({
            id: 'shared-care',
            title: 'Continue shared care with primary care or another service',
            when: 'If your kidney condition is stable enough for some monitoring to happen outside the specialist clinic',
          }),
          Object.freeze({
            id: 'earlier-review',
            title: 'Contact the kidney team earlier if you become unwell',
            stageId: 'ongoing-care',
            when: 'If symptoms, fluid balance, urine output or another concern changes in a way your team has told you needs review',
          }),
        ]),
      }),
    }),
  }),

  'mental-health': Object.freeze({
    assessment: Object.freeze({
      linkIds: Object.freeze(['appointment', 'urgent']),
      questionIndexes: Object.freeze([0, 1, 4]),
      roleIds: Object.freeze([
        'mental_health_nurse',
        'psychiatrist',
        'psychologist',
        'social_worker',
      ]),
      explanation: Object.freeze({
        whatHappens: 'A mental health assessment is a conversation about what has been happening and how you have been feeling. The team may ask about your physical health, medicines, sleep, daily life and the support around you. They may ask directly about your safety. This can include whether you feel at risk of harming yourself or someone else.',
        whyItMayHappen: 'The assessment helps the team understand what you are experiencing, how urgently you need support and what kind of help is most likely to be useful.',
        whatTeamIsDoing: 'The team is listening to your account, considering your strengths and difficulties, checking immediate safety and working with you to understand possible next steps. They may also ask whether there is someone you want involved in the assessment or care planning.',
        variationNote: 'There is no single test that explains every mental health problem. Assessments can happen in clinics, emergency settings, hospital wards, community services or by phone or video. If you feel in immediate danger or cannot keep yourself or someone else safe, use the urgent contact route from your local service or emergency services. Do not wait for a routine appointment.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'routine-support',
            title: 'Move to planned support or treatment',
            stageId: 'support',
            when: 'If the assessment shows that care can be provided through a planned outpatient or community route',
          }),
          Object.freeze({
            id: 'more-assessment',
            title: 'Have further assessment or input from another service',
            stageId: 'assessment',
            when: 'If the team needs more information or another specialist perspective before agreeing the plan',
          }),
          Object.freeze({
            id: 'urgent-support',
            title: 'Receive more urgent or intensive support',
            stageId: 'support',
            when: 'If the assessment identifies an immediate or significant safety concern or a need for closer support',
          }),
        ]),
      }),
    }),
    support: Object.freeze({
      linkIds: Object.freeze(['support', 'carers']),
      questionIndexes: Object.freeze([2, 3]),
      roleIds: Object.freeze([
        'mental_health_nurse',
        'psychiatrist',
        'psychologist',
        'occupational_therapist',
        'social_worker',
        'peer_support_worker',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Support can include talking therapies, medicines, practical or social support, occupational therapy, peer support or a combination of these. The team should explain the care plan, what each part is intended to help with and who your main contact is.',
        whyItMayHappen: 'Different mental health problems and life circumstances need different kinds of support. Treatment is usually more useful when it reflects your goals, preferences, strengths and the difficulties that matter most to you.',
        whatTeamIsDoing: 'The team is agreeing priorities with you, coordinating the people involved and reviewing whether support is helping. Where appropriate, they may involve family members, carers or other services with your agreement, while also considering confidentiality and safety.',
        variationNote: 'Not everyone needs medicines, therapy or the same intensity of support. Plans can change if your needs change or if an approach is not helping. Ask what choices are available and how your views are being included in decisions.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-plan',
            title: 'Continue the agreed care plan',
            stageId: 'support',
            when: 'If the support is helping and the current level of review remains appropriate',
          }),
          Object.freeze({
            id: 'adjust-support',
            title: 'Adjust the treatment or support plan',
            stageId: 'support',
            when: 'If symptoms, side effects, practical needs or your goals mean another approach may be more useful',
          }),
          Object.freeze({
            id: 'step-up-support',
            title: 'Increase the level of support',
            stageId: 'support',
            when: 'If your mental health or safety worsens and more frequent, urgent or intensive care is needed',
          }),
        ]),
      }),
    }),
    'next-steps': Object.freeze({
      linkIds: Object.freeze(['support', 'urgent']),
      questionIndexes: Object.freeze([5, 6]),
      roleIds: Object.freeze([
        'mental_health_nurse',
        'psychiatrist',
        'community_psychiatric_nurse',
        'social_worker',
        'peer_support_worker',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Ongoing care may include planned reviews, therapy, medicine checks or community support. You may also have a crisis plan explaining who to contact if your mental health becomes much worse. Some people have a named care coordinator or another main contact. This person can help different parts of your care work together.',
        whyItMayHappen: 'Mental health needs can change over time. A clear follow-up and crisis plan can make it easier to know what support is available, what to do if things worsen and when regular specialist care can safely reduce.',
        whatTeamIsDoing: 'The team is reviewing progress, checking whether the care plan still meets your needs and making sure responsibilities and contact routes are clear. They should explain what follow-up has actually been arranged and how to access more urgent help if needed.',
        variationNote: 'Some people need long-term specialist mental health care, while others move back to primary care or another community service after a period of support. If you feel in immediate danger or unable to keep yourself or someone else safe, seek urgent help using the local crisis or emergency route rather than waiting for planned follow-up.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-specialist-care',
            title: 'Continue specialist mental health follow-up',
            stageId: 'next-steps',
            when: 'If you still need regular specialist treatment, monitoring or coordination',
          }),
          Object.freeze({
            id: 'step-down-care',
            title: 'Move to less intensive or primary-care follow-up',
            when: 'If your needs are stable and specialist review can safely reduce',
          }),
          Object.freeze({
            id: 'urgent-reassessment',
            title: 'Seek urgent reassessment if safety or mental health deteriorates',
            stageId: 'next-steps',
            when: 'If there is a significant deterioration, an immediate safety concern or the existing plan is no longer enough',
          }),
        ]),
      }),
    }),
  }),
});

function patientSummary(explanation) {
  return [
    explanation?.whatHappens,
    explanation?.whyItMayHappen,
    explanation?.whatTeamIsDoing,
    explanation?.variationNote,
  ].filter(Boolean).join(' ');
}

export function applyJourneyBatch7Explanation(templateId, stages = []) {
  const stageMap = JOURNEY_BATCH7_EXPLANATIONS[templateId];
  if (!stageMap) return stages;
  const knownStageIds = stages.map((stage) => stage.id);
  return stages.map((stage) => {
    const enrichment = stageMap[stage.id];
    if (!enrichment) return stage;
    const enriched = normalizeJourneyStage({
      ...stage,
      ...enrichment,
      description: patientSummary(enrichment.explanation),
    });
    const errors = validateJourneyStageExplanation(enriched, knownStageIds);
    if (errors.length) throw new Error(`${templateId} stage ${stage.id}: ${errors.join('; ')}`);
    return enriched;
  });
}
